import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import apiClient from "@/api/client";
import { Button } from "@/components/ui/Button";
import { PageLoader } from "@/components/ui/Spinner";
import {
  PageHeader,
  SectionCard,
  Tabs,
} from "@/features/power_projects/components/ui";

// EZ-ARON knowledge base — purpose=ezamowienia entries served by the dlc_07-gated
// endpoint (backend forces purpose both ways; other ARONs never see these).

interface KbEntry {
  id: string;
  kind: string;
  title: string;
  body: string;
  status: string;
  scope: string;
  tags: string[];
  created_by_name: string | null;
  approved_by_name: string | null;
  why: string;
}

interface EffectivePreview {
  count: number;
  text: string;
}

const BASE = "/ezamowienia/knowledge";

const STATUS_LABEL: Record<string, string> = {
  active: "Aktywny",
  pending: "Oczekuje",
  rejected: "Odrzucony",
  archived: "Zarchiwizowany",
};

const KIND_LABEL: Record<string, string> = {
  guideline: "Zasada",
  error: "Błąd historyczny",
  false_positive: "Fałszywy alarm",
  note: "Notatka",
};

type TabKey = "entries" | "effective";

export default function EzKnowledgePage() {
  const qc = useQueryClient();
  const [tab, setTab] = useState<TabKey>("entries");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [kind, setKind] = useState("guideline");

  const entries = useQuery({
    queryKey: ["ezamowienia", "knowledge"],
    queryFn: async () => {
      const r = await apiClient.get(`${BASE}/`);
      return (r.data.data ?? []) as KbEntry[];
    },
  });

  const meta = useQuery({
    queryKey: ["ezamowienia", "knowledge-meta"],
    queryFn: async () => {
      const r = await apiClient.get(`${BASE}/meta/`);
      return r.data.data as { is_approver: boolean };
    },
  });

  const effective = useQuery({
    queryKey: ["ezamowienia", "knowledge-effective"],
    queryFn: async () => {
      const r = await apiClient.get(`${BASE}/effective/`);
      return r.data.data as EffectivePreview;
    },
    enabled: tab === "effective",
  });

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ["ezamowienia", "knowledge"] });

  const create = useMutation({
    mutationFn: async () => {
      await apiClient.post(`${BASE}/`, {
        kind,
        title: title.trim(),
        body: body.trim(),
        scope: "global",
        category: "other",
      });
    },
    onSuccess: () => {
      setTitle("");
      setBody("");
      invalidate();
    },
  });

  const lifecycle = useMutation({
    mutationFn: async ({
      id,
      verb,
    }: {
      id: string;
      verb: "accept" | "reject" | "archive";
    }) => {
      await apiClient.post(`${BASE}/${id}/${verb}/`, {});
    },
    onSuccess: invalidate,
  });

  if (entries.isLoading) return <PageLoader />;

  const rows = entries.data ?? [];
  const isApprover = meta.data?.is_approver ?? false;

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto">
      <PageHeader
        title="Baza wiedzy EZ-ARON"
        subtitle="Preferencje i reguły rynku przetargowego (np. „nie startujemy poniżej 200 tys.”, „zamawiający X notorycznie unieważnia”). AI czyta je przy klasyfikacji i rekomendacjach — podgląd w zakładce „Co widzi AI”."
        backTo="/ezamowienia"
        backLabel="Oferty przetargowe"
      />

      <Tabs<TabKey>
        tabs={[
          { key: "entries", label: `Wpisy (${rows.length})` },
          { key: "effective", label: "Co widzi AI" },
        ]}
        active={tab}
        onChange={setTab}
      />

      {tab === "entries" && (
        <>
          <SectionCard title="Nowy wpis" className="mb-4">
            <div className="space-y-2">
              <div className="flex gap-2">
                <select
                  value={kind}
                  onChange={(e) => setKind(e.target.value)}
                  className="border border-[var(--color-border)] rounded px-2 py-1.5 text-sm bg-transparent"
                >
                  <option value="guideline">Zasada</option>
                  <option value="error">Błąd historyczny</option>
                  <option value="false_positive">Fałszywy alarm</option>
                  <option value="note">Notatka</option>
                </select>
                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Tytuł (np. Remonty kotłowni nas nie interesują mimo CPV 45)"
                  className="flex-1 border border-[var(--color-border)] rounded px-2 py-1.5 text-sm bg-transparent"
                />
              </div>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={3}
                placeholder="Treść reguły — co EZ-ARON ma wiedzieć przy klasyfikacji i rekomendacjach…"
                className="w-full border border-[var(--color-border)] rounded px-2 py-1.5 text-sm bg-transparent"
              />
              <Button
                onClick={() => create.mutate()}
                disabled={create.isPending || !title.trim() || !body.trim()}
              >
                Dodaj wpis
              </Button>
              {create.isError && (
                <p className="text-sm text-red-600">
                  Nie udało się dodać wpisu.
                </p>
              )}
            </div>
          </SectionCard>

          <SectionCard title="Wpisy">
            {rows.length === 0 && (
              <p className="text-sm text-gray-400">
                Brak wpisów — dodaj pierwsze reguły rynku przetargowego.
              </p>
            )}
            <div className="space-y-3">
              {rows.map((e) => (
                <div
                  key={e.id}
                  className="border border-[var(--color-border)] rounded-lg p-3"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <span className="text-[10px] font-bold uppercase text-gray-400 mr-2">
                        {KIND_LABEL[e.kind] ?? e.kind}
                      </span>
                      <span className="font-medium">{e.title}</span>
                    </div>
                    <span
                      className={`text-[10px] rounded px-1.5 py-0.5 whitespace-nowrap ${
                        e.status === "active"
                          ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300"
                          : e.status === "pending"
                            ? "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"
                            : "bg-gray-100 text-gray-500 dark:bg-gray-800"
                      }`}
                    >
                      {STATUS_LABEL[e.status] ?? e.status}
                    </span>
                  </div>
                  <p className="text-sm mt-1 whitespace-pre-wrap">{e.body}</p>
                  <div className="flex items-center gap-2 mt-2 text-xs text-gray-400">
                    <span>{e.created_by_name ?? "—"}</span>
                    {isApprover && e.status === "pending" && (
                      <>
                        <Button
                          variant="secondary"
                          onClick={() =>
                            lifecycle.mutate({ id: e.id, verb: "accept" })
                          }
                        >
                          Zatwierdź
                        </Button>
                        <Button
                          variant="secondary"
                          onClick={() =>
                            lifecycle.mutate({ id: e.id, verb: "reject" })
                          }
                        >
                          Odrzuć
                        </Button>
                      </>
                    )}
                    {isApprover && e.status === "active" && (
                      <Button
                        variant="secondary"
                        onClick={() =>
                          lifecycle.mutate({ id: e.id, verb: "archive" })
                        }
                      >
                        Archiwizuj
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </SectionCard>
        </>
      )}

      {tab === "effective" && (
        <SectionCard
          title={`Efektywny zestaw reguł AI (${effective.data?.count ?? "…"})`}
        >
          {effective.isLoading ? (
            <PageLoader />
          ) : (
            <pre className="text-xs whitespace-pre-wrap font-mono bg-black/5 dark:bg-white/5 rounded p-3 overflow-x-auto">
              {effective.data?.text || "(pusto)"}
            </pre>
          )}
        </SectionCard>
      )}
    </div>
  );
}
